// @<COPYRIGHT>@
// ==================================================
// Copyright (c) 2023 Siemens
// Siemens Product Lifecycle Management Software Inc.
// All Rights Reserved.
// ==================================================
// @<COPYRIGHT>@

/**
 * This module is SWF3D Tcx Client
 *
 * @module js/swf3dTcxWorkspace
 */

import eventBus from 'js/eventBus';
import appCtxSvc from 'js/appCtxService';
import utils from 'js/swf3dWorkspaceUtils';
import { delegator, Logger } from '@swf3d/utils';
const logger = new Logger('workspace', 'swf3dTcxWorkspace');
import msgSvc from 'js/messagingService';

const workspaceEventBusKey = 'galaxy.workspace.';

/**
 * Generate line object data for aw-splm-table
 *
 * @param {object} partData
 */
function initPropData(partData) {
    return Object.keys(partData).reduce((prev, key) => {
        if (partData[key] instanceof Object && !Array.isArray(partData[key])) {
            let subList = initPropData(partData[key]);
            Object.assign(prev, subList);
        } else {
            const value = Array.isArray(partData[key]) ? JSON.stringify(partData[key]) : partData[key];
            const prop = {
                type: 'STRING',
                hasLov: false,
                isArray: false,
                displayValue: key,
                propertyName: key,
                propertyDisplayName: key,
                isEnabled: false,
                value: value,
                uiValue: value
            };
            if (key.toLowerCase() === 'id') {
                prop.type = 'object';
                prop.displayValue = '';
                prop.value = partData;
                prop.uiValue = '';
            } else if (key.endsWith('Date')) {
                prop.uiValue = new Date(value).toLocaleString();
            } else if (key.toLowerCase() === 'flpSize'.toLowerCase()) {
                let kbSize = Math.round(value / 1024.0);
                let strSize = kbSize + ' KB';
                if (kbSize > 900) {
                    strSize = Math.round((kbSize / 1024) * 1000) / 1000.0 + ' MB';
                }
                prop.uiValue = strSize;
            }
            prev[key] = prop;
        }
        return prev;
    }, {});
}

/**
 * Generate data to match aw-splm-table requirements.
 *
 * @param {Array} partlist - part list data from dm service
 * @returns {{list: object[]}} - formatted part list
 */
function getInitialData(partlist) {
    const list = partlist.map((s, i) => {
        return {
            type: 'Part',
            uid: s.id,
            typeIconURL: 'assets/image/swf3dPart48.png',
            props: initPropData(s)
        };
    });
    return { list };
}

/**
 * Search the part in the TCX and update the part list table.
 *
 * @memberof module:js/swf3dTcxWorkspace
 * @memberof js/swf3dTcxWorkspace
 * @function searchPart
 * @param {string} qString The query string, which also means `part item id` in dm service
 * @param {object} dataProvider viewmodel data provider
 * @returns void
 */
export let searchPart = (qString, dataProvider) => {
    if (!qString) {
        qString = '*';
    }
    if (dataProvider) {
        getPartList(qString).then(partList => {
            if (partList) {
                dataProvider.update(partList.list);
            }
        });
    }
};

const EMPTY_LIST = { list: [] };

/**
 * Query the parts from tcx by the given query name(part id).
 *
 * @function getPartList
 * @param {string} qString The item id
 * @returns {Promise<{list: object[]}>} - Promise of parts array
 */
export async function getPartList(qString) {
    if (!qString) {
        return EMPTY_LIST;
    }

    try {
        eventBus.publish('tcxDataIndicator-progress.start');
        const dbPartList = await delegator.API.searchParts(qString);
        return getInitialData(dbPartList);
    } catch (e) {
        // catch other errors except 404
        msgSvc.showError(`Fail get part list, ${e.statusText}`);
        return EMPTY_LIST;
    } finally {
        eventBus.publish('tcxDataIndicator-progress.end');
    }
}

/** The list of part types that're allowed to open */
const ALLOWED_PART_TYPES = ['NX', 'JT'];

/**
 * Select excutor.
 * For publish event to outside.
 *
 * @memberof module:js/swf3dTcxWorkspace
 * @function selectExecutor
 * @param {object} data - Selected row item
 * @param {object} viewModelData - The data in workspace view model
 * @returns void
 * @example
 * swf3dTcxWorkspace.selectExecutor({type: 'Project'}, viewModelData);
 */
function selectExecutor(data, viewModelData) {
    if (data.type === 'Folder') {
        // TODO: Get children of the selected folder
        // eventBus.publish(workspaceEventBusKey + 'onFolderSelected', data);
    } else {
        // part
        appCtxSvc.updatePartialCtx('galaxy.workspace.selectedObject', data);
        if (ALLOWED_PART_TYPES.includes(data.partType)) {
            // Add collab data.
            eventBus.publish(workspaceEventBusKey + 'onPartSelected', data);
        }
    }
}

/**
 * Do action when user clicks folder or part.
 *
 * @memberof module:js/swf3dTcxWorkspace
 * @function selectionChangedAction
 * @param {object} selected - Selected row item
 * @param {object} viewModelData - The data in workspace view model
 * @returns void
 * @example
 * swf3dTcxWorkspace.selectionChangedAction(selected, viewModelData);
 */
export const selectionChangedAction = (selected, viewModelData) => {
    if (utils.isPresent(selected)) {
        if (utils.isPresent(selected.props)) {
            // table mode
            const data = {
                urn: selected.uid,
                itemId: selected.props.itemId.value,
                rev: selected.props.revId.value,
                name: selected.props.name.value,
                type: selected.props.itemType.value,
                partType: selected.props.partType.value,
                date: selected.props.pdmLastModDate.value
            };

            selectExecutor(data, viewModelData);
        }
    }
};
